import React from "react";
import { Link } from "react-router-dom";
import { MapPin, PartyPopper, Handshake } from "lucide-react";
import { Reveal, Eyebrow } from "./Decorative";
import { INTEREST_ANCHOR } from "../lib/constants";
import { ROUTES } from "../lib/seo";
import { trackNotifyClick } from "../lib/track";

export const NEXT_ANCHOR = "whats-next";

/**
 * "What next" — the three ways in, straight under the home page hero once the
 * shows are sold out. Hosting leads: it is the site's first ask. Most visitors
 * arrive from the Instagram bio link on a phone, so each form is one tap from
 * here:
 *   - your occasion → /host/        (Form B, after the page's case)
 *   - your city     → the Form A on this same page (#interest)
 *   - a brand       → /sponsorship/ (Form C, right under its short hero)
 */
const PATHS = [
  {
    icon: PartyPopper,
    title: "Host Antara",
    body: "For your occasion: a wedding function, a sangeet, a housewarming, a corporate evening, a temple gathering.",
    cta: "Plan an evening",
    to: ROUTES.host.path,
    testid: "next-host",
  },
  {
    icon: MapPin,
    title: "Bring Antara to your city",
    body: "Tell us where you are — it is how we choose where Antara goes next.",
    cta: "Add my city",
    href: `#${INTEREST_ANCHOR}`,
    onClick: trackNotifyClick,
    testid: "next-city",
  },
  {
    icon: Handshake,
    title: "Partner with us",
    body: "Title, venue, hospitality, media or in-kind — for brands that want to be in the room.",
    cta: "Explore sponsorship",
    to: ROUTES.sponsorship.path,
    testid: "next-sponsor",
  },
];

export default function NextSteps() {
  return (
    <section
      id={NEXT_ANCHOR}
      className="scroll-mt-20 bg-[#0F0A07] px-5 pb-20 md:pb-28 pt-4"
      data-testid="next-steps"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>What happens next</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.08]" style={{ color: "#FBF6EE" }}>
              The next room is <span className="gold-grad italic">yours to fill</span>
            </h2>
          </div>
        </Reveal>
        <div className="mt-10 md:mt-14 grid md:grid-cols-3 gap-4 md:gap-6">
          {PATHS.map((p) => {
            const inner = (
              <>
                <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#E5C880] to-[#B8923E]">
                  <p.icon size={22} strokeWidth={1.4} color="#2A1F17" aria-hidden="true" />
                </span>
                <span className="mt-4 block font-display text-2xl md:text-[28px] leading-tight" style={{ color: "#FBF6EE" }}>
                  {p.title}
                </span>
                <span className="mt-2 block font-body text-[15px] leading-relaxed" style={{ color: "#E8D9C5" }}>
                  {p.body}
                </span>
                <span className="mt-5 inline-flex items-center gap-1.5 font-body text-sm font-semibold tracking-wide" style={{ color: "#E5C880" }}>
                  {p.cta} <span aria-hidden="true">→</span>
                </span>
              </>
            );
            const cls =
              "card-lift block h-full rounded-2xl border p-6 md:p-7 text-left transition " +
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#E5C880]";
            const style = { background: "#1C1A17", borderColor: "rgba(201,169,97,0.3)" };
            return p.to ? (
              <Link key={p.title} to={p.to} className={cls} style={style} data-testid={p.testid}>{inner}</Link>
            ) : (
              <a key={p.title} href={p.href} onClick={p.onClick} className={cls} style={style} data-testid={p.testid}>{inner}</a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
